// The two tile rows on Collings Properties and My Listings, and the one rule
// both pages use to decide which tile a property belongs to.
//
// Christian Gravias, 1 Oct 2026 (Monday 13122905047):
//   "Change the tiles:
//    All | For Sale | For Lease | Appraisal | Sold | Leased | Withdrawn
//    All | On-Market | Off-Market (THESE TILES ONLY POP UP IF THE AGENT CLICKS
//    FOR SALE OR FOR LEASE)
//    Please make this where an agent can click more then 1 tile on or off...
//    I have clicked For Sale and its showing me withdrawn properties, Thats not
//    for sale. We should only see withdrawn properties in the withdrawn tile"
//
// So the old Method of Sale + Listing Type pair is replaced by a Status row and
// a Market row. Both rows are multi-select: a property shows if it matches ANY
// lit Status tile, and the Market row narrows only the For Sale / For Lease
// tiles. The backend mirrors this exactly (`tiles` + `market` params on the
// property list endpoint), so page counts and the client-side check agree.
import { isCompletedListing, isLeaseListing, isLeasedListing, isSoldListing } from "./listingCompletionState";

export const STATUS_TILE_OPTIONS = [
    { id: "all", label: "All" },
    { id: "for_sale", label: "For Sale" },
    { id: "for_lease", label: "For Lease" },
    { id: "appraisal", label: "Appraisal" },
    // "Listed" is the won-but-not-yet-uploaded stage between Appraisal and Sold
    // (Christian Gravias, Monday 13225851756). Its own tile; excluded from For
    // Sale / For Lease since it isn't on the portals yet.
    { id: "listed", label: "Listed" },
    { id: "sold", label: "Sold" },
    // Marking a rental "Leased" reuses the sale plumbing (is_sold=true), so leased
    // rentals used to pile up on the Sold tab - Christian Gravias, 24 Sep 2026.
    // Sold is sales only and completed rentals live here.
    { id: "leased", label: "Leased" },
    { id: "withdrawn", label: "Withdrawn" },
];

export const MARKET_TILE_OPTIONS = [
    { id: "all", label: "All" },
    { id: "on_market", label: "On Market" },
    { id: "off_market", label: "Off Market" },
];

export const DEFAULT_STATUS_TILES = ["for_sale"];
export const DEFAULT_MARKET_TILES = ["all"];

// Click handling shared by both rows. "All" clears the row back to itself; any
// other tile switches on or off, and switching the last one off falls back to
// "All" so the row is never empty.
export const toggleTile = (current, id) => {
    if (id === "all") return ["all"];
    const withoutAll = current.filter((t) => t !== "all");
    const next = withoutAll.includes(id)
        ? withoutAll.filter((t) => t !== id)
        : [...withoutAll, id];
    return next.length ? next : ["all"];
};

// The Market row is pick-one. Joshua Diokno / Christian Gravias, 7 Oct 2026:
// "users are only able to click (highlight) either On Market or Off Market?
// Having them clicked at the same time is just the same as clicking All" -
// "Yes you are correct... you can only click one of them". Clicking the other
// tile swaps to it; clicking the lit one switches it off and falls back to All.
export const toggleSingleTile = (current, id) => {
    if (id === "all" || current.includes(id)) return ["all"];
    return [id];
};

// The Market row only means something for live listings, so it only shows
// while For Sale or For Lease is lit.
export const showsMarketRow = (statuses) =>
    statuses.includes("for_sale") || statuses.includes("for_lease");

const readStatusName = (p) =>
    (p?.status?.name || p?.status || p?.extra_info?.status || "").toString().toLowerCase();

export const isWithdrawnListing = (p) =>
    Boolean(p?.is_withdrawn || p?.extra_info?.is_withdrawn || readStatusName(p).includes("withdrawn"));

export const isAppraisalListing = (p) => readStatusName(p).includes("appraisal");

// The "Listed" status is an exact name (not a substring of "On/Off Market
// Listing", which contain "listing" not "listed").
export const isListedListing = (p) => readStatusName(p) === "listed";

// Still on the books: not sold, not leased, not withdrawn, not an appraisal, and
// not a Listed draft (it isn't on the portals yet, so it has its own tile and
// must not fall into For Sale). Withdrawing only flips is_withdrawn and leaves
// status.name as it was, so a withdrawn "On Market Listing" still reads as on
// market by name. Without the withdrawn check here it showed under For Sale,
// which is what Christian hit.
export const isLiveListing = (p) =>
    !isCompletedListing(p) && !isWithdrawnListing(p) && !isAppraisalListing(p) && !isListedListing(p);

// Collings Properties splits the market on "off market" in the status name,
// the same test the backend `market` param uses.
export const COLLINGS_MARKET_RULES = {
    isOffMarket: (p) => readStatusName(p).includes("off market"),
    isOnMarket: (p) => !readStatusName(p).includes("off market"),
};

// My Listings has always counted a Prospect as off market and needed an
// explicit "on market" status for On Market. Kept as it was.
export const MY_LISTINGS_MARKET_RULES = {
    isOffMarket: (p) => {
        const s = readStatusName(p);
        return s.includes("off market") || s.includes("prospect");
    },
    isOnMarket: (p) => readStatusName(p).includes("on market"),
};

const matchesMarket = (p, markets, rules) => {
    if (markets.includes("all")) return true;
    return (
        (markets.includes("on_market") && rules.isOnMarket(p)) ||
        (markets.includes("off_market") && rules.isOffMarket(p))
    );
};

// Does this property belong on screen for the lit tiles?
//
// "All" is every live listing (sale and lease, both markets). Sold, Leased,
// Withdrawn and Appraisal each live in their own tile. While the agent is
// searching, "All" lets everything through so a sold or withdrawn property can
// still be found by typing its address.
export const matchesTiles = (p, { statuses, markets, searching = false, marketRules = COLLINGS_MARKET_RULES }) => {
    if (statuses.includes("all")) return searching || isLiveListing(p);
    const live = isLiveListing(p);
    const lease = isLeaseListing(p);
    return (
        (statuses.includes("for_sale") && live && !lease && matchesMarket(p, markets, marketRules)) ||
        (statuses.includes("for_lease") && live && lease && matchesMarket(p, markets, marketRules)) ||
        (statuses.includes("appraisal") && isAppraisalListing(p) && !isWithdrawnListing(p)) ||
        (statuses.includes("listed") && isListedListing(p) && !isWithdrawnListing(p)) ||
        (statuses.includes("sold") && isSoldListing(p)) ||
        (statuses.includes("leased") && isLeasedListing(p)) ||
        (statuses.includes("withdrawn") && isWithdrawnListing(p))
    );
};
